'use client'

import type {
  StockStatusPosition,
  StockStatusProps,
  StockStatusType,
} from '@pearlconvert/ui/components/stock-status/stock-status.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import React from 'react'

import { Button } from '@pearlconvert/ui/components/button'
import { StockStatusSkeleton } from '@pearlconvert/ui/components/stock-status/stock-status-skeleton'
import { extractHeadingText } from '@pearlconvert/ui/utils/header-text.utils'

import '@pearlconvert/ui/components/stock-status/styles/stock-status.scss'

const StockStatus: React.FC<StockStatusProps> = (props: StockStatusProps) => {
  const t = useTranslations('commerce-blocks')
  const {
    isLoading,
    stock,
    customLabel,
    isLink,
    position = 'left' as StockStatusPosition,
    variant = 'one-line' as StockStatusType,
    onClickLink,
    showMissingItems,
  } = props

  if (isLoading) {
    return <StockStatusSkeleton position={position} variant={variant} />
  }

  if (!stock) {
    return null
  }

  const { statusCode, statusAdditionalInfo } = stock
  const label =
    customLabel || (statusCode ? t(`stock-status.${statusCode}`) : null)

  const accessibleLabel =
    extractHeadingText(customLabel) ||
    (statusCode ? t(`stock-status.${statusCode}`) : '')

  return (
    <div
      className={classNames('stock-status', {
        [`stock-status--${position}`]: position,
        'stock-status--two-lines': variant === 'two-lines',
        [`stock-status--${statusCode}`]: statusCode,
      })}
    >
      <span className="stock-status__info">
        {label && (
          <span className="stock-status__label">
            <span
              className="sr-only"
              role="status"
              aria-atomic="true"
            >{`${accessibleLabel}: ${
              statusCode === 'in-stock'
                ? t('stock-status.available')
                : t('stock-status.not-available')
            }`}</span>
            <span aria-hidden="true">{label}</span>
          </span>
        )}
        {statusAdditionalInfo && (
          <>
            {' '}
            <span className="stock-status__aside">
              {isLink || onClickLink ? (
                <Button
                  type="link"
                  className="stock-status__aside-button"
                  onClick={onClickLink}
                  url={isLink}
                  ariaLabel={`${accessibleLabel} ${showMissingItems ? t(`stock-status.expanded`) : t(`stock-status.collapsed`)}`}
                >
                  {statusAdditionalInfo}
                </Button>
              ) : (
                statusAdditionalInfo
              )}
            </span>
          </>
        )}
      </span>
    </div>
  )
}

export default StockStatus
